import React from 'react';
import PublicHeader from './PublicHeader';

interface LearnMorePageProps {
  onNavigateHome: () => void;
  onNavigateToRegister: () => void;
}

const SPECIES = ['Flathead', 'Bream', 'Mulloway', 'Whiting', 'Trevally', 'Tailor', 'Mangrove Jack'];

const LearnMorePage: React.FC<LearnMorePageProps> = ({ onNavigateHome, onNavigateToRegister }) => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {/* Header */}
      <PublicHeader title="Learn More" />

      {/* Hero */}
      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <img src="/tumbulgum-logo.png" alt="Tumbulgum Fishing Club" className="h-32 w-auto mx-auto mb-6" />
          <h1 className="text-4xl md:text-5xl font-bold text-fishing-blue mb-4">Welcome to Tumbulgum Fishing Club</h1>
          <p className="text-lg text-gray-600">Fishing, friendship and community on the beautiful Tweed River.</p>
        </div>
      </section>

      {/* About */}
      <section className="bg-blue-50">
        <div className="max-w-4xl mx-auto px-4 py-16">
          <h2 className="text-3xl font-bold text-fishing-blue mb-6 flex items-center gap-3">
            <span aria-hidden>🎣</span> About Us
          </h2>
          <div className="bg-white rounded-xl shadow-lg p-8 space-y-4 text-gray-700 leading-relaxed">
            <p className="text-xl font-semibold text-gray-900">So would you like to be a part of the BEST fishing club in Oz?</p>
            <p>Tumbulgum Fishing Club was established in 2022 by the locals wanting to support the community. We are a club that is more than just about fishing... we give back to our community. Our committee is a great bunch of volunteers that donate countless hours to make it all happen. We support local businesses and also donate to local charities and organisations such as VMR, Westpac Helicopter Service, local men's sheds and more.</p>
            <p className="text-xl font-semibold text-fishing-blue">In 2025 we boasted more than 400 financial members! Now that's an achievement! 🎉</p>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="bg-white">
        <div className="max-w-6xl mx-auto px-4 py-16">
          <h2 className="text-3xl font-bold text-fishing-blue mb-8 flex items-center gap-3">
            <span aria-hidden>🏆</span> How It Works
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { icon: '📅', text: 'Our club runs a fishing competition on the 3rd Sunday of every month, January to December (weather permitting).' },
              { icon: '🌅', text: 'Sign on is from 7am – 9am opposite the Tumbulgum Tavern. Here you will meet people, get a bacon & egg roll, a drink and most importantly, your entry to fish!' },
              { icon: '📸', text: 'We support catch & release, so all of our entries are done via a photo on a brag mat. It\'s a great way to do it, and of course keep a feed of fish if you like.' },
              { icon: '⏰', text: 'Entries to the competitions are accepted between 7am to 2pm.' },
              { icon: '🎁', text: 'Once all entries are adjudicated we have a fun, prize-filled presentation at 4pm (opposite Tumby Tavern) with a few refreshments. There will be 1st, 2nd, 3rd for all of the categories, mystery fish and a lucky member\'s draw.' },
            ].map((item, i) => (
              <div key={i} className="bg-white border border-gray-100 rounded-xl shadow-lg p-6 flex gap-4">
                <div className="text-3xl" aria-hidden>{item.icon}</div>
                <p className="text-gray-700 leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Species */}
      <section className="bg-blue-50">
        <div className="max-w-4xl mx-auto px-4 py-16">
          <h2 className="text-3xl font-bold text-fishing-blue mb-6 flex items-center gap-3">
            <span aria-hidden>🐟</span> Competition Species
          </h2>
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="flex flex-wrap gap-3">
              {SPECIES.map((s) => (
                <span key={s} className="px-4 py-2 bg-fishing-blue text-white rounded-full font-medium shadow">
                  {s}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="bg-white">
        <div className="max-w-6xl mx-auto px-4 py-16">
          <h2 className="text-3xl font-bold text-fishing-blue mb-8 flex items-center gap-3">
            <span aria-hidden>💰</span> Membership Pricing
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Family */}
            <div className="bg-white border border-gray-200 rounded-xl shadow-lg p-8 flex flex-col">
              <div className="text-4xl mb-3" aria-hidden>👨‍👩‍👧‍👦</div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Family Membership</h3>
              <p className="text-3xl font-bold text-fishing-blue mb-2">$120<span className="text-base font-normal text-gray-500">/year</span></p>
              <p className="text-gray-600 text-sm">Includes 2 adults and 3 juniors (16 or under on 1 January of the membership year).</p>
            </div>
            {/* Adult (highlighted) */}
            <div className="bg-white border-2 border-fishing-blue rounded-xl shadow-xl p-8 flex flex-col relative transform md:-translate-y-2">
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-fishing-blue text-white text-xs font-semibold px-3 py-1 rounded-full shadow">Most Popular</span>
              <div className="text-4xl mb-3" aria-hidden>🎣</div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Adult Individual</h3>
              <p className="text-3xl font-bold text-fishing-blue mb-2">$40<span className="text-base font-normal text-gray-500">/year</span></p>
              <p className="text-gray-600 text-sm">Full member benefits for individual adults.</p>
            </div>
            {/* Junior */}
            <div className="bg-white border border-gray-200 rounded-xl shadow-lg p-8 flex flex-col">
              <div className="text-4xl mb-3" aria-hidden>🧒</div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Junior Individual</h3>
              <p className="text-3xl font-bold text-fishing-blue mb-2">$30<span className="text-base font-normal text-gray-500">/year</span></p>
              <p className="text-gray-600 text-sm">For members under 18.</p>
            </div>
          </div>
          <div className="mt-8 bg-blue-50 rounded-xl p-6 text-center space-y-2">
            <p className="text-gray-700">Memberships are accepted on competition days at sign on between <strong>7am – 9am</strong>.</p>
            <p className="text-gray-700"><strong>Monthly Competition Entry</strong> — $10 per entrant (non-members $15).</p>
          </div>
          {/* How to pay — backlog #60: fees were already listed here, but
              nothing on the public site said HOW to pay until a member was
              already inside an active registration/check-in flow. This is
              the first place a prospective or returning member looks when
              checking fees, so it is also the right place to answer "how do
              I actually pay" before they ever start filling in a form. */}
          <div className="mt-4 bg-white border border-gray-200 rounded-xl p-6 text-center">
            <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center justify-center gap-2">
              <span aria-hidden>💳</span> How to pay
            </h3>
            <p className="text-gray-600 text-sm">
              Pay online by card when you register or check in, or pay by cash or EFTPOS
              at the sign-on desk on the day — both are accepted for memberships and
              competition entries.
            </p>
          </div>
        </div>
      </section>

      {/* Sponsorship */}
      <section className="bg-blue-50">
        <div className="max-w-4xl mx-auto px-4 py-16">
          <h2 className="text-3xl font-bold text-fishing-blue mb-6 flex items-center gap-3">
            <span aria-hidden>🤝</span> Sponsorship
          </h2>
          <div className="bg-white rounded-xl shadow-lg p-8 space-y-6">
            <p className="text-gray-700 leading-relaxed">
              We have sponsorship packages available to anybody that wants to help support our club and get their business out to our member base.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
                <div className="text-4xl mb-2" aria-hidden>🥇</div>
                <h3 className="font-bold text-gray-900">Gold</h3>
                <p className="text-2xl font-bold text-yellow-600">$2,500</p>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
                <div className="text-4xl mb-2" aria-hidden>🥈</div>
                <h3 className="font-bold text-gray-900">Silver</h3>
                <p className="text-2xl font-bold text-gray-500">$1,000</p>
              </div>
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-6 text-center">
                <div className="text-4xl mb-2" aria-hidden>🥉</div>
                <h3 className="font-bold text-gray-900">Bronze</h3>
                <p className="text-2xl font-bold text-orange-700">$500</p>
              </div>
            </div>
            <p className="text-gray-700">
              Get in touch to find out more at <a href="mailto:fishing@tumbyfc.com" className="text-fishing-blue font-medium hover:underline">fishing@tumbyfc.com</a>.
            </p>
          </div>
        </div>
      </section>

      {/* Closing + CTA */}
      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-4 py-16 text-center space-y-6">
          <p className="text-xl text-gray-700 leading-relaxed">
            Our club is about having fun, enjoying the outdoors, family time, community spirit, and hopefully catching a winning fish.
          </p>
          <p className="text-xl font-semibold text-fishing-blue leading-relaxed">
            So whether you just want to be a member, a sponsor, a volunteer or all of the above — we will welcome you to Tumbulgum Fishing Club with open arms.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
            <button
              onClick={onNavigateToRegister}
              className="px-8 py-3 bg-fishing-blue text-white rounded-lg font-semibold shadow-lg hover:bg-blue-700 transition"
            >
              Join Our Club
            </button>
            <a
              href="mailto:fishing@tumbyfc.com"
              className="px-8 py-3 border-2 border-fishing-blue text-fishing-blue rounded-lg font-semibold hover:bg-blue-50 transition"
            >
              Contact Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LearnMorePage;
